import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useWizard } from '@/common/wizard/wizard'
import { useAppContext } from '@/context/app'
import { groupLabel } from '@/components/usergroup/group'
import { rankingMachineLabel, RankingSelectionOptions, WizardData } from './Model'
import { MachineRankingExports } from './Exports'

function GroupSelectionState({ label, checked, partial }: {
  label: string; checked: boolean; partial: boolean
}) {
  const input = useRef<HTMLInputElement>(null)
  useEffect(() => { if (input.current) input.current.indeterminate = partial }, [partial])
  return <input ref={input} type='checkbox' checked={checked} disabled readOnly aria-readonly='true'
    aria-label={label} aria-checked={partial ? 'mixed' : checked} />
}

export function MachinesReportWizardSelection({ optionsURL = '/api/communal/my/reports/machinesranking/options', exports: Exports = MachineRankingExports }: {
  optionsURL?: string
  exports?: React.ComponentType<{ disabled?: boolean }>
} = {}) {
  const { services, translates } = useAppContext()
  const { wizardData, setWizardData, updateWizardData, prevStep, nextStep } = useWizard<WizardData>()
  const [options, setOptions] = useState<RankingSelectionOptions>()
  const [failed, setFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const [search, setSearch] = useState('')
  const t = (key: string) => translates?.for(`report.machinesranking.selection.${key}`)

  useEffect(() => {
    const controller = new AbortController()
    setFailed(false)
    setOptions(undefined)
    services.session.fetchWithToken(optionsURL, { signal: controller.signal })
      .then(response => {
        if (!response.ok) throw new Error('Cannot load machines')
        return response.json() as Promise<RankingSelectionOptions>
      }).then(data => {
        if (controller.signal.aborted) return
        const available = new Set(data.machines.map(machine => machine.id))
        setWizardData(current => ({ ...current, selectedMachineIds: current.selectedMachineIds === null
          ? [...available] : current.selectedMachineIds.filter(id => available.has(id)) }))
        setOptions(data)
      }).catch(() => { if (!controller.signal.aborted) setFailed(true) })
    return () => controller.abort()
  }, [services, attempt, setWizardData, optionsURL])

  const selected = new Set(wizardData.selectedMachineIds || [])
  const groupMachines = useMemo(() => {
    const result = new Map<string, string[]>()
    for (const machine of options?.machines || []) {
      for (const groupId of machine.groupIds) {
        const ids = result.get(groupId) || []
        ids.push(machine.id)
        result.set(groupId, ids)
      }
    }
    return result
  }, [options])
  const toggle = (ids: string[], checked: boolean) => {
    const next = new Set(wizardData.selectedMachineIds || [])
    ids.forEach(id => checked ? next.add(id) : next.delete(id))
    updateWizardData({ selectedMachineIds: [...next] })
  }
  // Empty groups are not useful here. Only groups returned by the ACL-scoped API
  // can appear, and remaining machines have an explicit bulk action of their own.
  const groups = (options?.groups || []).filter(group => groupMachines.has(group.id)).map(group => ({
    id: group.id, label: groupLabel(group), machineIds: groupMachines.get(group.id)!
  }))
  const visibleGroupIds = new Set(groups.map(group => group.id))
  const ungroupedIds = (options?.machines || []).filter(machine => !machine.groupIds.some(id => visibleGroupIds.has(id))).map(machine => machine.id)
  if (ungroupedIds.length) groups.push({ id: 'ungrouped', label: t('ungrouped') || '', machineIds: ungroupedIds })
  const back = <button type='button' className='btn btn-secondary' onClick={prevStep}>{t('back')}</button>

  if (failed) return <div className='ranking-selection'><p role='alert'>{t('load_error')}</p>
    <div className='right-btns'>{back}<button type='button' className='btn btn-next' onClick={() => setAttempt(value => value + 1)}>{t('retry')}</button></div></div>
  if (!options) return <div className='ranking-selection'><p role='status'>{t('loading')}</p>{back}</div>

  const query = search.trim().toLocaleLowerCase()
  const filtered = options.machines.filter(machine =>
    [rankingMachineLabel(machine), machine.serialNumber].join(' ').toLocaleLowerCase().includes(query))
  const empty = selected.size === 0
  return <div className='ranking-selection'>
    <header className='ranking-selection-heading'>
      <h3>{t('title')}</h3>
      <p>{t('hint')}</p>
    </header>
    <div className='ranking-selection-toolbar'>
      <p className='ranking-selection-count' role='status'>{t('selected')}: <strong>{selected.size} / {options.machines.length}</strong></p>
      {options.machines.length > 0 && <div className='ranking-selection-tools'>
        <button type='button' className='btn btn-secondary' onClick={() => toggle(options.machines.map(machine => machine.id), true)}>{t('select_all')}</button>
        <button type='button' className='btn btn-secondary' onClick={() => updateWizardData({ selectedMachineIds: [] })}>{t('deselect_all')}</button>
      </div>}
    </div>
    {options.machines.length === 0 ? <p>{t('no_machines')}</p> : <>
      <section className='ranking-selection-panel' aria-labelledby='ranking-groups-title'>
        <h4 id='ranking-groups-title'>{t('groups')}</h4>
        <div className='ranking-selection-groups'>
          {groups.map(group => {
            const ids = group.machineIds
            const count = ids.filter(id => selected.has(id)).length
            return <div key={group.id} className='ranking-selection-group' role='group' aria-label={group.label}>
              <div className='ranking-selection-group-info'>
                <GroupSelectionState label={group.label} checked={count === ids.length} partial={count > 0 && count < ids.length} />
                <span className='ranking-selection-group-name'>{group.label}</span>
                <span className='ranking-selection-group-count'>{count} / {ids.length}</span>
              </div>
              <div className='ranking-selection-group-actions'>
                <button type='button' className='btn btn-secondary' aria-label={`${t('select_all')}: ${group.label}`} onClick={() => toggle(ids, true)}>{t('select_all')}</button>
                <button type='button' className='btn btn-secondary' aria-label={`${t('deselect_all')}: ${group.label}`} onClick={() => toggle(ids, false)}>{t('deselect_all')}</button>
              </div>
            </div>
          })}
        </div>
      </section>
      <section className='ranking-selection-panel' aria-labelledby='ranking-machines-title'>
        <h4 id='ranking-machines-title'>{t('machines')}</h4>
        <label className='ranking-selection-search'>{t('search')}
          <input type='search' value={search} onChange={e => setSearch(e.target.value)} />
        </label>
        <div className='ranking-selection-machines' role='group' aria-label={t('title')}>
          {filtered.map(machine => <label key={machine.id} className={selected.has(machine.id) ? 'is-selected' : ''}>
            <input type='checkbox' checked={selected.has(machine.id)} onChange={e => toggle([machine.id], e.target.checked)} />
            <span>{rankingMachineLabel(machine)}</span>
          </label>)}
          {filtered.length === 0 && <p>{t('no_results')}</p>}
        </div>
      </section>
    </>}
    {empty && options.machines.length > 0 && <p role='alert'>{t('empty')}</p>}
    <div className='right-btns ranking-selection-footer'>
      {back}
      <button type='button' className='btn btn-next' disabled={empty} onClick={nextStep}>{t('show_report')}</button>
      <Exports disabled={empty} />
    </div>
  </div>
}
